"use client";

import React from "react";
import { ArrowRight, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
const nearbyAreas = [{
  name: "Woking",
  to: "/AreasWeCoverPage"
}, {
  name: "Camberley",
  to: "/AreasWeCoverPage"
}, {
  name: "Ascot",
  to: "/AreasWeCoverPage"
}, {
  name: "Bracknell",
  to: "/AreasWeCoverPage"
}, {
  name: "Windsor",
  to: "/AreasWeCoverPage"
}, {
  name: "Guildford",
  to: "/AreasWeCoverPage"
}];
const accreditations = ["NASC Member", "CHAS Accredited"];
export default function QuoteCta() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="finalquotecta" data-nav="light" className="relative bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={prefersReducedMotion ? false : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative overflow-hidden rounded-2xl bg-secondary text-secondary-foreground shadow-xl border border-border/20 p-8 sm:p-12 lg:p-16">
          {/* Subtle background glow effect using theme color */}
          <div className="absolute top-0 right-0 -mt-16 -mr-16 h-80 w-80 rounded-full bg-primary/10 blur-3xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            {/* Left Content Column */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-md bg-accent/20 px-3 py-1 text-xs font-semibold tracking-wide uppercase text-accent-foreground mb-6">
                <ShieldCheck className="size-4 text-primary" />
                <span>Safety-led scaffolding contractor</span>
              </div>

              <h2 className="text-3xl font-bold tracking-tight text-secondary-foreground sm:text-4xl md:text-5xl font-default">
                Request a scaffolding quote
              </h2>

              <p className="mt-4 max-w-xl text-base text-muted-foreground sm:text-lg">
                Speak directly with Pete and our estimating team for technical guidance, site visits, and clear commercial pricing.
              </p>

              {/* Accreditations Row */}
              <div className="mt-8 flex flex-wrap items-center gap-4 text-xs font-medium text-muted-foreground">
                {accreditations.map((badge, i) => <span key={badge} data-index={i} className="inline-flex items-center gap-1.5 rounded-md border border-border/40 bg-secondary-foreground/5 px-2.5 py-1">
                    <span className="size-1.5 rounded-full bg-primary" />
                    {badge}
                  </span>)}
              </div>
            </div>

            {/* Right Action Column */}
            <div className="flex flex-col gap-4 lg:col-span-5">
              <div className="rounded-xl border border-border/20 bg-card p-6 text-card-foreground shadow-md">
                <p className="text-sm font-semibold text-card-foreground mb-4">
                  Direct contact details
                </p>

                <div className="flex flex-col gap-3">
                  <a href="tel:01344929900" className="group flex items-center justify-between rounded-lg border border-border/60 bg-background p-3.5 text-foreground transition-colors hover:border-primary hover:bg-muted/50 hover:text-foreground">
                    <div className="flex items-center gap-3">
                      <div className="flex size-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <Phone className="size-4" />
                      </div>
                      <div className="text-left">
                        <span className="block text-xs text-muted-foreground">Call our team</span>
                        <span className="font-mono text-sm font-semibold text-foreground">01344 929900</span>
                      </div>
                    </div>
                    <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                  </a>

                  <a href="mailto:quotes@bmfscaffolding.co.uk" className="group flex items-center justify-between rounded-lg border border-border/60 bg-background p-3.5 transition-colors hover:border-primary hover:bg-muted/50">
                    <div className="flex items-center gap-3">
                      <div className="flex size-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <Mail className="size-4" />
                      </div>
                      <div className="text-left">
                        <span className="block text-xs text-muted-foreground">Email specifications</span>
                        <span className="text-sm font-semibold text-foreground">quotes@bmfscaffolding.co.uk</span>
                      </div>
                    </div>
                    <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                  </a>
                </div>

                <div className="mt-5">
                  <Button asChild size="lg" className="w-full font-semibold shadow-md">
                    <Link to="/ContactRequestQuotePage">
                      Request a quote
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <Separator className="my-8 bg-border/20" />

          {/* Nearby Areas List */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-sm">
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground shrink-0">
              <MapPin className="size-3.5 text-primary" />
              <span>Nearby areas:</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {nearbyAreas.map((area, index) => <div key={area.name} data-index={index} className="flex items-center gap-4">
                  <Link to={area.to} className="text-secondary-foreground/80 transition-colors hover:text-primary underline-offset-4 hover:underline">
                    {area.name}
                  </Link>
                  {index < nearbyAreas.length - 1 && <span className="text-muted-foreground/40 select-none">•</span>}
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}